import React, { useState, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, inpS, btnP, btnSec } from "../../constants/styles";
import Fld from "../common/Fld";
import LoadBtn from "../common/LoadBtn";
import useUnsavedWarning from "../../hooks/useUnsavedWarning";
import { formatPropAddr } from "../../utils/propertyAddr";
import { isCommercial } from "../../utils/propertyTypes";

function LeaseFormPage({ edit }) {
  const { t, h, props, tens, leases, setLeases, go, selLeaseId, flash, refreshData } = useContext(Ctx);
  const ex = edit ? leases.find(l => l.id === selLeaseId) : null;

  const [property_id, setPid] = useState(ex?.property_id || "");
  const [tenant_id, setTid] = useState(ex?.tenant_id || "");
  const [lease_type, setLT] = useState(ex?.lease_type || "residential");
  const [term_type, setTT] = useState(ex ? (ex.end_date ? "fixed_term" : "month_to_month") : "month_to_month");
  const [start_date, setSD] = useState(ex?.start_date || "");
  const [end_date, setED] = useState(ex?.end_date || "");
  const selProp = props.find(p => p.id === property_id);
  const [rent, setRent] = useState(ex?.rent?.toString() || (selProp?.rent?.toString() || ""));
  const [deposit, setDep] = useState(ex?.deposit?.toString() || (selProp?.dep?.toString() || ""));
  const [notice_period_days, setNPD] = useState(ex?.notice_period_days?.toString() || "30");
  const [lock_in_months, setLock] = useState(ex?.lock_in_months?.toString() || "");
  const [escalation_pct, setEsc] = useState(ex?.escalation_pct?.toString() || "");
  const [escalation_interval_months, setEI] = useState(ex?.escalation_interval_months?.toString() || "12");
  const [status, setStatus] = useState(ex?.status || "draft");
  const [err, setErr] = useState("");

  useUnsavedWarning(property_id !== (ex?.property_id || "") || tenant_id !== (ex?.tenant_id || "") || start_date !== (ex?.start_date || ""));

  // Auto-fill rent/deposit when property changes (only if user hasn't manually set values)
  const handlePropChange = (pid) => {
    setPid(pid);
    const p = props.find(x => x.id === pid);
    if (p) {
      if (!rent || rent === (selProp?.rent?.toString() || "")) setRent(p.rent?.toString() || "");
      if (!deposit || deposit === (selProp?.dep?.toString() || "")) setDep(p.dep?.toString() || "");
      // Auto-detect lease_type from property_type
      if (p.property_type) setLT(isCommercial(p.property_type) ? "commercial" : "residential");
    }
  };

  // Tenants already on a lease (excluding current lease in edit mode)
  const assignedTids = leases.filter(l => l.status === "active" && (!edit || l.id !== selLeaseId)).map(l => l.tenant_id);

  const save = async () => {
    if (!property_id || !tenant_id || !start_date || !rent) { setErr(t.fillRequired); return; }
    const leaseObj = {
      id: edit ? selLeaseId : `lease_${Date.now()}`,
      property_id, tenant_id, lease_type, start_date,
      end_date: term_type === "fixed_term" ? end_date : "",
      rent: Number(rent), deposit: Number(deposit) || 0,
      notice_period_days: Number(notice_period_days) || 30,
      lock_in_months: lock_in_months ? Number(lock_in_months) : null,
      escalation_pct: escalation_pct ? Number(escalation_pct) : null,
      escalation_interval_months: escalation_interval_months ? Number(escalation_interval_months) : 12,
      status,
      created_at: ex?.created_at || new Date().toISOString()
    };
    await api.saveLease(leaseObj);
    setLeases([...leases.filter(l => l.id !== leaseObj.id), leaseObj]);
    await refreshData();
    flash(edit ? t.leaseUpdated : t.leaseCreated);
    go("agreement");
  };

  const radioS = (active) => ({ padding: "8px 16px", borderRadius: 20, border: `1px solid ${active ? h.ac : h.bd}`, background: active ? `${h.ac}15` : "transparent", color: active ? h.ac : h.sc, cursor: "pointer", fontSize: 12, fontWeight: active ? 600 : 400 });
  const lblS = { color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 };

  return <div style={{ paddingTop: 16 }}>
    <button onClick={() => go("agreement")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>{edit ? t.editLease : t.createLease}</h2>
    <div style={{ ...crd(h), padding: 20 }}>
      {err && <div style={{ color: h.rd, fontSize: 12, marginBottom: 10, padding: "8px 12px", background: `${h.rd}10`, borderRadius: 8 }}>{err}</div>}

      {/* Property */}
      <label style={lblS}>{t.property}</label>
      <select value={property_id} onChange={e => handlePropChange(e.target.value)} style={{ ...inpS(h), cursor: "pointer" }}>
        <option value="">-- {t.property} --</option>
        {props.map(p => <option key={p.id} value={p.id}>{formatPropAddr(p)}</option>)}
      </select>

      {/* Tenant */}
      <label style={lblS}>{t.tenant}</label>
      <select value={tenant_id} onChange={e => setTid(e.target.value)} style={{ ...inpS(h), cursor: "pointer" }}>
        <option value="">-- {t.selectTenant} --</option>
        {tens.map(te => <option key={te.id} value={te.id} style={{ color: assignedTids.includes(te.id) ? h.sc : h.tx }}>{te.name}{assignedTids.includes(te.id) ? "" : " *"}</option>)}
      </select>

      {/* Lease Type */}
      <label style={lblS}>{t.leaseType}</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button type="button" onClick={() => setLT("residential")} style={radioS(lease_type === "residential")}>{t.residential}</button>
        <button type="button" onClick={() => setLT("commercial")} style={radioS(lease_type === "commercial")}>{t.commercial}</button>
      </div>

      {/* Term Type */}
      <label style={lblS}>{t.termType}</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button type="button" onClick={() => setTT("month_to_month")} style={radioS(term_type === "month_to_month")}>{t.monthToMonth}</button>
        <button type="button" onClick={() => setTT("fixed_term")} style={radioS(term_type === "fixed_term")}>{t.fixedTerm}</button>
      </div>

      {/* Start Date */}
      <label style={lblS}>{t.startDate}</label>
      <input type="date" value={start_date} onChange={e => setSD(e.target.value)} style={inpS(h)} />

      {/* End Date (only for fixed term) */}
      {term_type === "fixed_term" && <>
        <label style={lblS}>{t.endDate}</label>
        <input type="date" value={end_date} onChange={e => setED(e.target.value)} style={inpS(h)} />
      </>}

      {/* Rent & Deposit */}
      <Fld l={t.leaseRent} v={rent} set={setRent} h={h} type="number" />
      <Fld l={t.leaseDeposit} v={deposit} set={setDep} h={h} type="number" />

      {/* Notice Period */}
      <label style={lblS}>{t.noticePeriod} ({t.days})</label>
      <input type="number" value={notice_period_days} onChange={e => setNPD(e.target.value)} style={inpS(h)} />

      {/* Lock-in */}
      <label style={lblS}>{t.lockIn} ({t.months})</label>
      <input type="number" value={lock_in_months} onChange={e => setLock(e.target.value)} placeholder="0" style={inpS(h)} />

      {/* Escalation */}
      <Fld l={t.escalationPct} v={escalation_pct} set={setEsc} h={h} type="number" ph="e.g. 5" />
      <label style={lblS}>{t.escalationInterval}</label>
      <input type="number" value={escalation_interval_months} onChange={e => setEI(e.target.value)} style={inpS(h)} />

      {/* Status */}
      <label style={lblS}>{t.leaseStatus}</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button type="button" onClick={() => setStatus("draft")} style={radioS(status === "draft")}>{t.leaseDraft}</button>
        <button type="button" onClick={() => setStatus("active")} style={radioS(status === "active")}>{t.leaseActive}</button>
      </div>

      {/* Save / Cancel */}
      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
        <LoadBtn onClick={save} label={t.save} style={{ ...btnP(h), flex: 1 }} />
        <button onClick={() => go("agreement")} style={{ ...btnSec(h), flex: 1 }}>{t.cancel}</button>
      </div>
    </div>
  </div>;
}

export default LeaseFormPage;
